import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export type OverviewStateId =
  | 'zero'
  | 'strategy'
  | 'ads_only'
  | 'seo_only'
  | 'active_30d'
  | 'ongoing';

export interface OverviewFlags {
  stateId: OverviewStateId;
  hasStrategy: boolean;
  strategyInProgress: boolean;
  hasAds: boolean;
  hasAdsAndStrategy: boolean;
  hasSEO: boolean;
  hasGsc: boolean;
  hasGbp: boolean;
  hasGoogleAds: boolean;
  hasMetaAds: boolean;
  isActive: boolean;
  isOngoing: boolean;
  isZero: boolean;
}

export interface OverviewAttentionItem {
  id: string;
  icon: string;
  label: string;
  cta: string | null;
  ctaHref: string | null;
  dest: string | null;
}

export interface OverviewFindability {
  clicks: number | null;
  clicksTrendPct: number | null;
  clicksSparkline: number[] | null;
  aeoReadiness: 'high' | 'medium' | 'low' | null;
  aeoSignalsPassed: number | null;
  aeoSignalsTotal: number | null;
  aeoSparkline: number[] | null;
  visibilityScore: number | null;
  visibilityGrade: string | null;
  visibilityDelta: number | null;
}

export interface OverviewReputationReview {
  stars: number;
  text: string;
  reviewerName: string | null;
  platform: string;
  date: string | null;
  hasReply: boolean;
  replyText: string | null;
}

export interface OverviewReputation {
  rating: number | null;
  reviewCount: number | null;
  distribution: { star: 5 | 4 | 3 | 2; pct: number }[];
  recentReviews: OverviewReputationReview[];
  repliedCount: number;
  totalDisplayed: number;
  source: 'gbp' | 'public_scan' | null;
  newLast30Days: number | null;
  neighborhoodAvgReviews: number | null;
  neighborhoodAvgRating: number | null;
  neighborhoodCity: string | null;
}

export interface OverviewMarketingCampaignRow {
  name: string;
  platform: string;
  status: 'Active' | 'Paused';
  spend: number;
  conversions: number;
  cpa: number | null;
  strategyName: string | null;
}

export interface OverviewMarketing {
  totalSpend: number | null;
  conversions: number | null;
  costPerConversion: number | null;
  campaigns: OverviewMarketingCampaignRow[];
  hasStrategy: boolean;
  daysSinceLaunch: number | null;
}

export interface OverviewCompareCompetitor {
  name: string;
  rating: number | null;
  reviews: number | null;
  isYou: boolean;
}

export interface OverviewCompareZip {
  zip: string;
  label: string;
  pctPatients: number;
  pctHispanic: number | null;
  medIncome: string | null;
  isHome: boolean;
}

export interface OverviewCompare {
  competitors: OverviewCompareCompetitor[];
  zips: OverviewCompareZip[];
  gapInsight: string | null;
}

export interface OverviewPractice {
  name: string;
  locationLabel: string | null;
  period: string;
}

export interface OverviewData {
  flags: OverviewFlags;
  practice: OverviewPractice;
  attention: OverviewAttentionItem[];
  findability: OverviewFindability;
  reputation: OverviewReputation;
  marketing: OverviewMarketing;
  compare: OverviewCompare;
}

export const overviewApi = {
  get: async (
    locationId: string,
    demoState?: OverviewStateId,
  ): Promise<ApiResponse<OverviewData>> => {
    const response = await apiClient.get<ApiResponse<OverviewData>>('/dashboard/overview', {
      params: { locationId, ...(demoState ? { demoState } : {}) },
    });
    return response.data;
  },
};
